/**
 * Module « imports » — IMP-01 / IMP-02 (ARCHITECTURE §8, §9.2).
 *
 * Une fois, avant la première campagne : Anne confie des années de données à
 * un logiciel neuf. Deux onglets :
 *  - Abonnés (IMP-01/02) : le fichier modèle, la simulation, la
 *    correspondance des formules, la validation ;
 *  - Historique des lots déjà passés, rapport consultable.
 *
 * ★★ « L’IMPORT DES ABONNÉS N’EST PAS OBLIGATOIRE, JE DOIS DISPOSER DES ÉCRANS
 * POUR LE FAIRE. » — L’onglet s’intitulait « Abonnés (obligatoire) ». C’était
 * faux, et exactement à l’envers de ce que le produit permet : le chemin manuel
 * est complet (créer une personne, lui créer une souscription, lui affecter ses
 * places) et il vit sous Guichet → Souscriptions. Un mot dans un onglet
 * transformait un raccourci en péage. L’onglet dit désormais ce qu’il est — une
 * reprise de fichier — et l’en-tête nomme l’autre chemin, avec ses liens.
 *
 * ★★ L’ONGLET « PIÈCES VUES (OPTIONNEL) » A ÉTÉ RETIRÉ (IMP-03).
 * « Je ne comprends pas les pièces vues… pour moi inutile. » La DONNÉE reste,
 * elle : `piece_vue_importee` alimente l’historique du portail abonné (POR-04)
 * et les admissions au contrôle d’accès continuent de l’écrire (SCAN-08). La
 * route `POST /imports/historique` reste servie. C’est l’ÉCRAN d’import qui
 * disparaît — le seul endroit où cette notion était demandée à quelqu’un qui
 * n’en avait pas besoin.
 */

import { lazy } from 'react'
import { Link } from 'react-router'
import type { RouteObject } from 'react-router'

import { EcranSansObjet } from '../../components/EcranSansObjet'
import { CHEMINS } from '../../lib/chemins'
import { Tabs } from '../../ui/Tabs'

const ImportAbonnesPanel = lazy(() =>
  import('./components/ImportAbonnesPanel').then((m) => ({ default: m.ImportAbonnesPanel })),
)
const HistoriqueLots = lazy(() =>
  import('./components/HistoriqueLots').then((m) => ({ default: m.HistoriqueLots })),
)

function PageImports() {
  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Imports</h1>
        <p className="mt-1 text-sm text-slate-500">
          Reprenez les abonnés de la saison passée en deux temps — simulation puis validation — pour
          que la première campagne démarre comme si le logiciel avait toujours été là.
        </p>
      </header>

      {/* ★ Le chemin manuel, nommé et cliquable : cet écran est un raccourci,
          pas un passage obligé. */}
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <p className="max-w-prose">
          <strong className="text-slate-900">Cet import n’est pas obligatoire.</strong> Il fait
          gagner des heures quand on a un fichier ; sans fichier, tout se saisit à la main et rien
          n’est perdu — on{' '}
          <Link to={`${CHEMINS.souscriptions}/personnes`} className="underline">
            crée une personne
          </Link>
          , puis on lui{' '}
          <Link to={`${CHEMINS.souscriptions}/nouvelle`} className="underline">
            crée une souscription
          </Link>{' '}
          et on lui affecte ses places sur le plan, abonné par abonné.
        </p>
      </div>

      <Tabs
        onglets={[
          { id: 'abonnes', libelle: 'Reprise d’un fichier', contenu: <ImportAbonnesPanel /> },
          { id: 'lots', libelle: 'Historique des imports', contenu: <HistoriqueLots /> },
        ]}
      />
    </div>
  )
}

/**
 * ★ EVT-04 — cet écran entier suppose une SAISON PASSÉE : un fichier d'abonnés,
 * des formules à faire correspondre, des sièges occupés l'an dernier. Un
 * organisateur de fête d'entreprise n'a rien de tout cela, et son tenant naît
 * sans formule ni souscription (`plateforme/provisionnement.ts`). L'entrée est
 * retirée de son menu (`components/navigation.ts`) ; ce renvoi ferme l'adresse
 * gardée en signet, qui reste sinon le dernier chemin vers l'écran.
 */
export const routesImports: RouteObject[] = [
  {
    index: true,
    element: (
      <EcranSansObjet vers={CHEMINS.admin}>
        <PageImports />
      </EcranSansObjet>
    ),
  },
]
